import type { KeyboardEvent, JSX } from 'react';
import { useEffect, useRef, useContext, useState } from 'react';
import type { ButtonProps } from '@wistia/vhs';
import { Button, ButtonGroup, useOnClickOutside } from '@wistia/vhs'; // Ensure ButtonGroup is imported
import { isNotNil, isNotUndefined } from '~/utilities/type-guards';
import type { GetTargetFn, Trackable } from '~/onboarding/types';
import { useSnowplow } from '~/onboarding/hooks/useSnowplow/useSnowplow';
import { isUndefined } from 'lodash';
import { useLogger } from '../../hooks/useLogger';
import { withErrorBoundary, useErrorBoundary } from '../../hooks/useErrorBoundary';
import { OnboardingContext } from '../../hooks/useOnboardingContext/OnboardingContext';
import { TooltipContent } from './shared/TooltipContent';
import { Footer, StyledTooltip, TooltipContainer, StepCounter } from './styles'; // Ensure StepCounter is imported
import { TooltipTargetContainer } from './shared/TooltipTargetContainer';
import type { SharedTooltipProps } from './types';
import { TARGET_CLICK } from './types';
import { useTargetInteraction } from '../../hooks/useTargetInteraction';

export type TooltipProps = SharedTooltipProps &
  Trackable & {
    /**
     * Thumbnail image displayed at the top of the tooltip
     */
    image?: JSX.Element;
    /**
     * Main content of the tooltip
     */
    content?: JSX.Element;
    /**
     * Label with purple sparkle
     */
    upgradeText?: string;
    /**
     * Title content for the tooltip
     */
    title?: JSX.Element | string;
    /**
     * Whether or not this tooltip is dismissible
     */
    dismissible?: boolean;
    /**
     * Custom text for the dismiss button
     */
    dismissText?: string;
    /**
     * Position of the button in the footer
     */
    buttonPosition?: 'apart' | 'end' | 'start';
    /**
     * Overrides for the button in the footer
     */
    buttonOverrides?: ButtonProps;
    /**
     * Goes to the next step of the onboarding featureId on dismiss
     */
    nextOnDismiss?: boolean;
    /**
     * Used internally for attaching Tooltip to locally rendered Hotspot
     */
    getRelativeTarget?: GetTargetFn;
    /**
     * renders 'back' button
     */
    backText?: string;
    /**
     * Goes to previous step of the onboarding featureId on back
     */
    onBack?: () => void;
    /**
     * Renders number for the current step
     */
    currentStep?: number;
    /**
     * Renders total number of steps
     */
    totalSteps?: number;
    /**
     * Top position of the tooltip
     */
    hotspotTop?: number;
    /**
     * Left position of the tooltip
     */
    hotspotLeft?: number;
    /**
     * Whether or not to use the new tooltip design
     */
    onboardingRedesign?: boolean;
  };

export const TooltipComponent = ({
  getTarget,
  startActive = false,
  direction,
  image,
  content,
  children,
  upgradeText,
  title,
  onDismiss,
  dismissible = false,
  dismissText,
  hideOnClickOutside = true,
  snowplowParams,
  buttonOverrides,
  nextOnDismiss,
  getRelativeTarget,
  backText,
  onBack,
  currentStep,
  totalSteps,
  hotspotTop,
  hotspotLeft,
  onboardingRedesign = false,
}: TooltipProps): JSX.Element | null => {
  const context = useContext(OnboardingContext);
  const currentFeatureId = context?.getCurrentFeatureId();
  const logger = useLogger(currentFeatureId);
  useErrorBoundary((error: Error) => logger.error(error));
  const [active, setActive] = useState(startActive);
  useTargetInteraction(() => setActive(true), getRelativeTarget ?? getTarget);
  const { logEvent, logEventOnce } = useSnowplow();

  useEffect(() => {
    if (active) {
      logEventOnce('tooltipView', snowplowParams);
    }
  }, [active, logEventOnce, snowplowParams]);

  const handleDismiss = () => {
    logEvent('tooltipDismiss', snowplowParams);
    setActive(false);
    if (isNotNil(nextOnDismiss) && isNotNil(currentFeatureId)) {
      context?.next(currentFeatureId);
    }
    onDismiss?.();
  };

  const handleBack = () => {
    logEvent('multiStepTooltipClickBack', snowplowParams);
    onBack?.();
  };

  const tooltipRef = useRef(null);
  useOnClickOutside(tooltipRef, () => {
    if (active && hideOnClickOutside) {
      setActive(false);
    }
  });

  const keyDownHandler = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setActive(false);
    }
  };

  useEffect(() => {
    const onTargetClick = (event: CustomEvent) => {
      const currentTarget = getRelativeTarget?.() ?? getTarget?.();
      if (currentTarget !== event.target) {
        setActive(false);
      }
    };
    document.body.addEventListener(TARGET_CLICK, onTargetClick);
    return () => {
      document.body.removeEventListener(TARGET_CLICK, onTargetClick);
    };
  }, [setActive, getTarget, getRelativeTarget]);

  const showFooter =
    isNotNil(onDismiss) ||
    dismissible ||
    isNotNil(onBack) ||
    (isNotNil(currentStep) && isNotNil(totalSteps));

  return (
    <TooltipTargetContainer
      direction={direction}
      getTarget={getTarget}
      getRelativeTarget={getRelativeTarget}
      hotspotTop={hotspotTop}
      hotspotLeft={hotspotLeft}
    >
      <TooltipContainer
        ref={hideOnClickOutside ? tooltipRef : null}
        active={active}
        aria-hidden={!active}
        data-testid="tooltip-content"
        direction={direction}
        isAbsolute={isNotUndefined(getTarget) && isUndefined(getRelativeTarget)}
        role="tooltip"
      >
        <StyledTooltip onboardingRedesign={onboardingRedesign}>
          <TooltipContent
            image={image}
            content={content}
            upgradeText={upgradeText}
            title={title}
          />
          {showFooter ? (
            <Footer>
              {isNotNil(currentStep) && isNotNil(totalSteps) ? (
                <StepCounter>
                  {currentStep} of {totalSteps}
                </StepCounter>
              ) : (
                <span />
              )}
              <ButtonGroup>
                {isNotNil(onBack) && isNotNil(backText) && (
                  <Button
                    data-testid="tooltip-back"
                    label={backText}
                    onClick={handleBack}
                    size="sm"
                    variant="secondary"
                  />
                )}
                {(isNotNil(onDismiss) || dismissible) && (
                  <Button
                    data-testid="tooltip-dismiss"
                    label={dismissText ?? 'Got it'}
                    onClick={handleDismiss}
                    size="sm"
                    variant="primary"
                    {...buttonOverrides}
                  />
                )}
              </ButtonGroup>
            </Footer>
          ) : null}
        </StyledTooltip>
      </TooltipContainer>
      {children !== null && (
        <div
          data-testid="tooltip-trigger"
          onClick={() => setActive(true)}
          onKeyDown={keyDownHandler}
          onFocus={() => setActive(true)}
          role="button"
          tabIndex={0}
        >
          {children}
        </div>
      )}
    </TooltipTargetContainer>
  );
};

/**
 * @deprecated Use OnboardingPopover or OnboardingMultiStepPopover instead.
 */
export const Tooltip = withErrorBoundary(TooltipComponent);
